import { useCallback, useState } from "react";
import type {
  HeadshotProposal,
  ImportSharePointHeadshotsResponse,
} from "@event-planner/shared";
import {
  listSharePointHeadshots,
  importSharePointHeadshots,
} from "../services/headshotService";

/**
 * State hooks for the headshot-library pull dialog (ADR-178, production
 * retarget ADR-181).
 *
 * `unconfigured` distinguishes the failed-precondition degrade (library
 * not set up for this environment — expected until the client-tenant ceremony runs)
 * from real errors, mirroring the HubSpot Stage 2 pattern.
 */

function isFailedPrecondition(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    String((err as { code: unknown }).code).includes("failed-precondition")
  );
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong.";
}

export function useHeadshotProposals() {
  const [proposals, setProposals] = useState<HeadshotProposal[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [libraryUrl, setLibraryUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [unconfigured, setUnconfigured] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnconfigured(null);
    try {
      const response = await listSharePointHeadshots();
      setProposals(response.proposals);
      setTotalFiles(response.totalFiles);
      setUnmatchedCount(response.unmatchedCount);
      setLibraryUrl(response.libraryUrl);
    } catch (err) {
      if (isFailedPrecondition(err)) {
        setUnconfigured(errorMessage(err));
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    proposals,
    totalFiles,
    unmatchedCount,
    libraryUrl,
    loading,
    unconfigured,
    error,
    load,
  };
}

/** The callable schema caps a batch at 100 items — larger approvals run as sequential chunks. */
const IMPORT_CHUNK_SIZE = 100;

export function useHeadshotImportApply() {
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [result, setResult] =
    useState<ImportSharePointHeadshotsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback(
    async (items: Array<{ driveItemId: string; personId: string }>) => {
      setApplying(true);
      setError(null);
      const chunks: Array<typeof items> = [];
      for (let i = 0; i < items.length; i += IMPORT_CHUNK_SIZE) {
        chunks.push(items.slice(i, i + IMPORT_CHUNK_SIZE));
      }
      const aggregate: ImportSharePointHeadshotsResponse = {
        success: true,
        imported: 0,
        failed: 0,
        results: [],
      };
      setProgress(chunks.length > 1 ? { done: 0, total: chunks.length } : null);
      try {
        for (const chunk of chunks) {
          const response = await importSharePointHeadshots({ items: chunk });
          aggregate.imported += response.imported;
          aggregate.failed += response.failed;
          aggregate.results.push(...response.results);
          setProgress((prev) =>
            prev ? { done: prev.done + 1, total: prev.total } : prev,
          );
        }
        setResult(aggregate);
        return aggregate;
      } catch (err) {
        // A failed chunk aborts the remaining ones, but chunks that already
        // ran DID import — surface the partial outcome, not just the error.
        const remaining = items.length - aggregate.results.length;
        setError(
          errorMessage(err) +
            (remaining > 0
              ? ` (${remaining} item${remaining === 1 ? "" : "s"} not attempted)`
              : ""),
        );
        if (aggregate.results.length > 0) {
          setResult(aggregate);
          return aggregate;
        }
        return null;
      } finally {
        setApplying(false);
        setProgress(null);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(null);
  }, []);

  return { apply, applying, progress, result, error, reset };
}
